import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  FormContainer,
  FormSection,
  FloatingField,
  InputText,
  Select,
} from '../../components/aip';
import { createAiManager, getLlmModels, type LlmModel } from '../../services/manageNewApi';

/**
 * AIマネージャー新規作成ページ
 */
export const AiManagerCreatePage: React.FC = () => {
  const navigate = useNavigate();

  // データ状態
  const [llmModels, setLlmModels] = useState<LlmModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // フォーム状態
  const [name, setName] = useState('');
  const [personaSummary, setPersonaSummary] = useState('');
  const [analysisPrompt, setAnalysisPrompt] = useState('');
  const [executionIntervalSeconds, setExecutionIntervalSeconds] = useState('60');
  const [llmModelId, setLlmModelId] = useState<string>('');
  const [sortOrder, setSortOrder] = useState('0');

  // LLMモデル取得
  useEffect(() => {
    const fetchModels = async () => {
      try {
        setLoading(true);
        const models = await getLlmModels();
        setLlmModels(models);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'LLMモデルの取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    fetchModels();
  }, []);

  // 保存処理
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('名前は必須です');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      await createAiManager({
        name: name.trim(),
        persona_summary: personaSummary.trim() || undefined,
        analysis_prompt: analysisPrompt.trim() || undefined,
        execution_interval_seconds: parseInt(executionIntervalSeconds, 10) || 60,
        llm_model_id: llmModelId ? parseInt(llmModelId, 10) : undefined,
        sort_order: parseInt(sortOrder, 10) || 0,
      });

      navigate('/meeting/manage-new/ai-managers');
    } catch (err) {
      setError(err instanceof Error ? err.message : '作成に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  // LLMモデルのオプション
  const llmModelOptions = [
    { value: '', label: '選択しない' },
    ...llmModels.map((model) => ({
      value: String(model.id),
      label: model.api_model_name_display,
    })),
  ];

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
        読み込み中...
      </div>
    );
  }

  return (
    <>
      {error && (
        <div
          style={{
            padding: '0.75rem 1rem',
            backgroundColor: 'var(--color-red50)',
            color: 'var(--color-alert)',
            borderRadius: '0.5rem',
            marginBottom: '1rem',
          }}
        >
          {error}
        </div>
      )}

      <FormContainer onSubmit={handleSubmit}>
        <FormSection title="基本情報" layout="vertical">
          <FloatingField label="名前" htmlFor="name" required>
            <InputText
              id="name"
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="AIマネージャーの名前"
            />
          </FloatingField>

          <FloatingField label="ペルソナ概要" htmlFor="persona_summary">
            <textarea
              id="persona_summary"
              name="persona_summary"
              className="aip-input-text"
              value={personaSummary}
              onChange={(e) => setPersonaSummary(e.target.value)}
              placeholder="このAIマネージャーの役割や特徴を記述"
              rows={3}
              style={{ resize: 'vertical', minHeight: '80px' }}
            />
          </FloatingField>

          <FloatingField label="分析プロンプト" htmlFor="analysis_prompt">
            <textarea
              id="analysis_prompt"
              name="analysis_prompt"
              className="aip-input-text"
              value={analysisPrompt}
              onChange={(e) => setAnalysisPrompt(e.target.value)}
              placeholder="会議分析時に使用するプロンプト"
              rows={5}
              style={{ resize: 'vertical', minHeight: '120px' }}
            />
          </FloatingField>
        </FormSection>

        <FormSection title="設定">
          <FloatingField label="LLMモデル" htmlFor="llm_model_id">
            <Select
              id="llm_model_id"
              name="llm_model_id"
              value={llmModelId}
              onChange={(e) => setLlmModelId(e.target.value)}
              options={llmModelOptions}
            />
          </FloatingField>

          <FloatingField label="実行間隔（秒）" htmlFor="execution_interval_seconds">
            <InputText
              id="execution_interval_seconds"
              name="execution_interval_seconds"
              type="number"
              value={executionIntervalSeconds}
              onChange={(e) => setExecutionIntervalSeconds(e.target.value)}
              min="10"
              max="3600"
            />
          </FloatingField>

          <FloatingField label="表示順" htmlFor="sort_order">
            <InputText
              id="sort_order"
              name="sort_order"
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              min="0"
            />
          </FloatingField>
        </FormSection>

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <Button
            buttonType="outline"
            size="md"
            type="button"
            onClick={() => navigate('/meeting/manage-new/ai-managers')}
            disabled={saving}
          >
            キャンセル
          </Button>
          <Button buttonType="solid-fill" size="md" type="submit" disabled={saving}>
            {saving ? '作成中...' : '作成'}
          </Button>
        </div>
      </FormContainer>
    </>
  );
};

export default AiManagerCreatePage;

